import React from "react";
import {Button, Dropdown, Modal, Tooltip} from "antd";
import type {MenuProps} from "antd";
import {CheckCircleOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, MoreOutlined} from "@ant-design/icons";
import {motion} from "framer-motion";
import moment from "moment";
import AppEntity from "../../types/AppEntity";
import {base64ToDataUrl, formatBytes, formatTime} from "../../services/Helpers";
import {platformInfo} from "../../services/Platform";
import downloadApp from "../../services/DownloadApp";
import PlatformBadge from "./PlatformBadge";
import StatusIcon from "./StatusIcon";
import styles from "./Apps.module.css";

interface AppRowProps {
    app: AppEntity
    isAdmin: boolean
    isThisDevice: boolean
    onEdit: (app: AppEntity) => void
    onRelease: (app: AppEntity) => void
    onDelete: (app: AppEntity) => void
}

export default function AppRow({app, isAdmin, isThisDevice, onEdit, onRelease, onDelete}: AppRowProps) {
    const released = !!app.publishedAt;
    const icon = app.iconBase64EncodedContent ? base64ToDataUrl(app.iconBase64EncodedContent, app.iconMediaType) : undefined;
    const date = released ? app.publishedAt : app.createdAt;

    const confirmDelete = () => {
        Modal.confirm({
            title: `Delete ${app.name} ${app.version}?`,
            content: released
                ? "Staff will no longer be able to download this build. This can't be undone."
                : "The draft and its file will be removed. This can't be undone.",
            okText: "Delete",
            okButtonProps: {danger: true},
            cancelText: "Cancel",
            onOk: () => onDelete(app),
        });
    };

    const menu: MenuProps["items"] = [
        ...(!released ? [{key: "release", icon: <CheckCircleOutlined/>, label: "Release to staff", onClick: () => onRelease(app)}] : []),
        {key: "edit", icon: <EditOutlined/>, label: "Edit details", onClick: () => onEdit(app)},
        {type: "divider" as const},
        {key: "delete", icon: <DeleteOutlined/>, label: "Delete", danger: true, onClick: confirmDelete},
    ];

    return (
        <motion.li
            layout="position"
            layoutId={isAdmin ? `app-${app.id}` : undefined}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.2, ease: [0.16, 1, 0.3, 1]}}
            className={`${styles.row} ${isAdmin ? styles.rowAdmin : ""}`}
        >
            {isAdmin && <span className={styles.cellStatus}><StatusIcon released={released}/></span>}

            <span className={styles.appIcon}>
                {icon ? <img src={icon} alt=""/> : <span aria-hidden>{app.name.charAt(0)}</span>}
            </span>

            <div className={styles.cellName}>
                <div className={styles.nameLine}>
                    <span className={styles.name}>{app.name}</span>
                    {isThisDevice && <span className={styles.deviceHint} title="Fits your device">Your device</span>}
                </div>
                <p className={styles.description}>{app.description}</p>
                <p className={styles.metaLine}>
                    <span>{platformInfo(app.platform).name}</span>
                    <span className="mono">{app.version}</span>
                    <span>{formatBytes(app.fileSize)}</span>
                    <span>{moment(date).fromNow()}</span>
                </p>
            </div>

            <span className={styles.cellPlatform}><PlatformBadge platform={app.platform}/></span>
            <span className={`${styles.cellVersion} mono`}>{app.version}</span>
            <span className={`${styles.cellSize} mono`}>{formatBytes(app.fileSize)}</span>
            <Tooltip title={`${released ? "Released" : "Uploaded"} ${formatTime(date)}`}>
                <span className={styles.cellDate}>{moment(date).fromNow()}</span>
            </Tooltip>

            <div className={styles.cellActions}>
                {isAdmin && !released && (
                    <Button size="small" onClick={() => onRelease(app)} className={styles.releaseButton}>
                        Release
                    </Button>
                )}
                <Button
                    size="small"
                    icon={<DownloadOutlined/>}
                    onClick={() => downloadApp(app)}
                    aria-label={`Download ${app.name} ${app.version}`}
                    className={styles.downloadButton}
                >
                    <span className={styles.downloadLabel}>Download</span>
                </Button>
                {isAdmin && (
                    <Dropdown menu={{items: menu}} trigger={["click"]} placement="bottomRight">
                        <Button size="small" type="text" icon={<MoreOutlined/>} aria-label={`More actions for ${app.name}`}/>
                    </Dropdown>
                )}
            </div>
        </motion.li>
    );
}
